import { debounce } from 'lodash';

/**
 * Google Tag Manager utility functions
 *
 * Usage:
 * - Track page views: gtm.trackPageView('/path')
 * - Track events: gtm.trackEvent('event_name', { key: 'value' })
 * - Track custom data: gtm.pushData({ customData: 'value' })
 */

/**
 * Push data to GTM dataLayer
 * @param {Object} data - Data object to push to dataLayer
 */
export const pushData = (data) => {
    if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push(data);
    }
};

/**
 * Track a page view
 * @param {string} path - The page path
 * @param {string} title - Optional page title
 */
export const trackPageView = (path, title = null) => {
    const pageTitle =
        title ||
        (typeof document !== 'undefined' && document.title ? document.title : '');
    const pageLocation =
        typeof window !== 'undefined' && window.location && window.location.href
            ? window.location.href
            : '';

    pushData({
        event: 'page_view',
        page_path: path,
        page_title: pageTitle,
        page_location: pageLocation
    });
};

/**
 * Track a custom event
 * @param {string} eventName - Name of the event
 * @param {Object} eventData - Additional event data
 */
export const trackEvent = (eventName, eventData = {}) => {
    pushData({
        event: eventName,
        ...eventData
    });
};

/**
 * Track user login (NM-142)
 * @param {Object} userData - User information
 * @param {string} [method='password'] - Login method ('password' | 'SSO')
 */
export const trackLogin = (userData = {}, method = 'password') => {
    const userRegion = userData.aRegion || null;
    const userRegionValue = Array.isArray(userRegion) ? userRegion.join(', ') : userRegion;

    const organisation = userData.aRegion || null;
    const organisationValue = Array.isArray(organisation) ? organisation.join(', ') : organisation;

    trackEvent('login', {
        user_id: userData._id || userData.id,
        user_role: userData.sUserRole,
        organisation: organisationValue || '',
        method,
        user_region: userRegionValue
    });
};

/**
 * Track user logout
 */
export const trackLogout = () => {
    trackEvent('logout');
};

/**
 * Track document view
 * @param {Object} documentData - Document information
 * @param {string} documentData.id - Document ID
 * @param {string} documentData.title - Document title
 * @param {string} documentData.file - Document file
 * @param {string|Array} documentData.documentType - Document type(s)
 * @param {string|Array} documentData.documentRegion - Document region(s)
 * @param {string|Array} documentData.userRegion - User region(s)
 * @param {string} [documentData.userId] - Current user's ID
 * @param {string} [documentData.userRole] - Current user's role
 * @param {string|Array} [documentData.organisation] - Organisation (mapped from user's aRegion)
 * @param {string|Array} [documentData.technicalSubject] - Document technical subject(s) (aTechnical) — emitted as `technical_subject`
 * @param {string|Array} [documentData.documentTags] - Document tags — emitted as `document_tags` (comma-joined); source-field TBC (spec referenced doc.aTags which does not exist on the schema)
 * @param {string|Array} [documentData.documentGeneration] - Document generation(s) (aGenerations)
 * @param {string|Array} [documentData.documentLanguage] - Document language(s) (sDocumentLanguage)
 */
export const trackDocumentView = (documentData = {}) => {
    const documentRegion = documentData.documentRegion || null;
    const documentRegionValue = Array.isArray(documentRegion) ? documentRegion.join(', ') : documentRegion;

    const userRegion = documentData.userRegion || null;
    const userRegionValue = Array.isArray(userRegion) ? userRegion.join(', ') : userRegion;

    const documentType = documentData.documentType || null;
    const documentTypeValue = Array.isArray(documentType) ? documentType.join(', ') : documentType;

    const organisation = documentData.organisation || null;
    const organisationValue = Array.isArray(organisation) ? organisation.join(', ') : organisation;

    const technicalSubject = documentData.technicalSubject || null;
    const technicalSubjectValue = Array.isArray(technicalSubject) ? technicalSubject.join(', ') : technicalSubject;

    const documentTags = documentData.documentTags || null;
    const documentTagsValue = Array.isArray(documentTags) ? documentTags.join(', ') : documentTags;

    const documentGeneration = documentData.documentGeneration || null;
    const documentGenerationValue = Array.isArray(documentGeneration) ? documentGeneration.join(', ') : documentGeneration;

    const documentLanguage = documentData.documentLanguage || null;
    const documentLanguageValue = Array.isArray(documentLanguage) ? documentLanguage.join(', ') : documentLanguage;

    trackEvent('document_view', {
        document_id: documentData.id,
        document_title: documentData.title,
        user_id: documentData.userId,
        user_role: documentData.userRole,
        organisation: organisationValue || '',
        document_file: documentData.file,
        document_type: documentTypeValue || '',
        document_region: documentRegionValue || '',
        user_region: userRegionValue || '',
        technical_subject: technicalSubjectValue || '',
        document_tags: documentTagsValue || '',
        document_generation: documentGenerationValue || '',
        document_language: documentLanguageValue || ''
    });
};

/**
 * Track document download (NM-141)
 * @param {Object} documentData - Document information
 * @param {string} documentData.id - Document ID
 * @param {string} documentData.title - Document title
 * @param {string} documentData.userId - Current user's ID
 * @param {string} documentData.userRole - Current user's role
 * @param {string|Array} documentData.organisation - Organisation (mapped from user's aRegion)
 * @param {string} [documentData.file] - Document file (supplementary)
 * @param {string|Array} [documentData.documentType] - Document type(s) (supplementary)
 * @param {string|Array} [documentData.documentRegion] - Document region(s) (supplementary)
 * @param {string|Array} [documentData.userRegion] - User region(s) (supplementary)
 * @param {string|Array} [documentData.technicalSubject] - Document technical subject(s) (aTechnical) — emitted as `technical_subject`
 * @param {string|Array} [documentData.documentTags] - Document tags — emitted as `document_tags`; source-field TBC (spec's doc.aTags does not exist)
 * @param {string|Array} [documentData.documentGeneration] - Document generation(s) (aGenerations)
 * @param {string|Array} [documentData.documentLanguage] - Document language(s) (sDocumentLanguage)
 */
export const trackDocumentDownload = (documentData = {}) => {
    const documentRegion = documentData.documentRegion || null;
    const documentRegionValue = Array.isArray(documentRegion) ? documentRegion.join(', ') : documentRegion;

    const userRegion = documentData.userRegion || null;
    const userRegionValue = Array.isArray(userRegion) ? userRegion.join(', ') : userRegion;

    const documentType = documentData.documentType || null;
    const documentTypeValue = Array.isArray(documentType) ? documentType.join(', ') : documentType;

    const organisation = documentData.organisation || null;
    const organisationValue = Array.isArray(organisation) ? organisation.join(', ') : organisation;

    const technicalSubject = documentData.technicalSubject || null;
    const technicalSubjectValue = Array.isArray(technicalSubject) ? technicalSubject.join(', ') : technicalSubject;

    const documentTags = documentData.documentTags || null;
    const documentTagsValue = Array.isArray(documentTags) ? documentTags.join(', ') : documentTags;

    const documentGeneration = documentData.documentGeneration || null;
    const documentGenerationValue = Array.isArray(documentGeneration) ? documentGeneration.join(', ') : documentGeneration;

    const documentLanguage = documentData.documentLanguage || null;
    const documentLanguageValue = Array.isArray(documentLanguage) ? documentLanguage.join(', ') : documentLanguage;

    trackEvent('document_download', {
        document_id: documentData.id,
        document_title: documentData.title,
        user_id: documentData.userId,
        user_role: documentData.userRole,
        organisation: organisationValue || '',
        document_file: documentData.file,
        document_type: documentTypeValue || '',
        document_region: documentRegionValue || '',
        user_region: userRegionValue || '',
        technical_subject: technicalSubjectValue || '',
        document_tags: documentTagsValue || '',
        document_generation: documentGenerationValue || '',
        document_language: documentLanguageValue || ''
    });
};

/**
 * Track document search (internal function - use trackSearchDebounced for debounced version)
 * @param {string} searchTerm - Search query
 * @param {number} resultCount - Number of results
 */
const trackSearchInternal = (searchTerm, resultCount = 0) => {
    trackEvent('search', {
        search_term: searchTerm,
        result_count: resultCount
    });
};

/**
 * Track document search (debounced version - waits 500ms after last call)
 * @param {string} searchTerm - Search query
 * @param {number} resultCount - Number of results
 */
export const trackSearch = debounce(trackSearchInternal, 500);

/**
 * Track document search (immediate version - no debounce)
 * @param {string} searchTerm - Search query
 * @param {number} resultCount - Number of results
 */
export const trackSearchImmediate = trackSearchInternal;

/**
 * Track library filter apply (internal - use trackLibraryFilter for debounced version)
 * @param {Object} filterData - Filter information
 * @param {string} filterData.filterType - One of: 'brand' | 'generation' | 'technical' | 'language' | 'document_type' | 'document_format' | 'date_range' | 'search'
 * @param {string|Array} filterData.filterValue - The applied filter value (arrays comma-joined)
 * @param {string} [filterData.userId] - Current user's ID
 * @param {string} [filterData.userRole] - Current user's role
 * @param {string|Array} [filterData.organisation] - Organisation (mapped from user's aRegion)
 * @param {string|Array} [filterData.userRegion] - User region(s)
 */
const trackLibraryFilterInternal = (filterData = {}) => {
    const filterValue = filterData.filterValue;
    const filterValueString = Array.isArray(filterValue) ? filterValue.join(', ') : filterValue;

    const organisation = filterData.organisation || null;
    const organisationValue = Array.isArray(organisation) ? organisation.join(', ') : organisation;

    const userRegion = filterData.userRegion || null;
    const userRegionValue = Array.isArray(userRegion) ? userRegion.join(', ') : userRegion;

    trackEvent('library_filter_applied', {
        filter_type: filterData.filterType,
        filter_value: filterValueString || '',
        user_id: filterData.userId,
        user_role: filterData.userRole,
        organisation: organisationValue || '',
        user_region: userRegionValue || ''
    });
};

/**
 * Track library filter apply (debounced 500ms - use for typed inputs like search)
 */
export const trackLibraryFilter = debounce(trackLibraryFilterInternal, 500);

/**
 * Track library filter apply (immediate - use for discrete filter changes like dropdowns/checkboxes)
 */
export const trackLibraryFilterImmediate = trackLibraryFilterInternal;

const gtm = {
    pushData,
    trackPageView,
    trackEvent,
    trackLogin,
    trackLogout,
    trackDocumentView,
    trackDocumentDownload,
    trackSearch,
    trackLibraryFilter,
    trackLibraryFilterImmediate
};

export default gtm;
